import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Tab, Nav, Form } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { ExploreResponse, FolderNode } from '../Models/FileShare';
import { exploreFileShare } from '../Services/FileShareApiService';
import { FadeAlert } from '../Components/Global/Transitions';
import { LoaderIcon } from '../Components/Global/Icons';
import RecentActivity from '../Components/Global/RecentActivity';
import { FolderBrowser } from '../Components/FileTransfer/FolderBrowser';

const EMPTY_NODE: FolderNode = { files: [], folders: {} };

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const formatProjectDisplayName = (projectId: string, projectName?: string): string => {
  const fallback = (projectName ?? projectId ?? '').trim();
  if (!fallback || !projectId) return fallback;

  const idPrefixPattern = new RegExp(`^${escapeRegExp(projectId)}\\s*[-_:]\\s*`, 'i');
  const stripped = fallback.replace(idPrefixPattern, '').trim();
  return stripped || fallback;
};

const FileTransferPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [exploreData, setExploreData] = useState<ExploreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'myfiles' | 'shared' | 'projects'>('myfiles');

  const accountId = accounts[0]?.homeAccountId;

  const fetchData = useCallback(async () => {
    if (!accounts[0]) return;
    setLoading(true);
    setError(false);
    try {
      const data = await exploreFileShare(instance, accounts[0]);
      setExploreData(data);
      const firstProject = Object.keys(data.projectNames)[0];
      if (firstProject && !selectedProjectId) setSelectedProjectId(firstProject);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [instance, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshData = useCallback(async () => {
    if (!accounts[0]) return;
    try {
      const data = await exploreFileShare(instance, accounts[0]);
      setExploreData(data);
    } catch {
      setError(true);
    }
  }, [instance, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <LoaderIcon />;

  const hasNoProjects = Object.keys(exploreData?.projectNames ?? {}).length === 0;

  return (
    <Row>
      <Col sm={12} as='h1'>File Transfer</Col>
      <Col xl={9}>
        <FadeAlert show={error} dismissFunction={() => setError(false)} />

        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k as typeof activeTab)}>
          <Nav variant='tabs' className='mb-3'>
            <Nav.Item><Nav.Link eventKey='myfiles'>My Files</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey='shared'>Shared</Nav.Link></Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey='projects'
                disabled={hasNoProjects}
                title={hasNoProjects ? 'No projects available' : undefined}
              >
                Projects
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey='myfiles'>
              <FolderBrowser
                key='myfiles'
                rootNode={exploreData?.users[exploreData.userId] ?? EMPTY_NODE}
                label='My Files'
                section='users'
                onRefresh={refreshData}
                instance={instance}
                account={accounts[0]}
                uploading={uploading}
                setUploading={setUploading}
                setError={setError}
              />
            </Tab.Pane>

            <Tab.Pane eventKey='shared'>
              <FolderBrowser
                key='shared'
                rootNode={exploreData?.shared ?? EMPTY_NODE}
                label='Shared'
                section='shared'
                onRefresh={refreshData}
                instance={instance}
                account={accounts[0]}
                uploading={uploading}
                setUploading={setUploading}
                setError={setError}
              />
            </Tab.Pane>

            <Tab.Pane eventKey='projects'>
              <Form.Select
                value={selectedProjectId}
                onChange={e => setSelectedProjectId(e.target.value)}
                style={{ maxWidth: 300 }}
                className='mb-3'
              >
                {Object.entries(exploreData?.projectNames ?? {}).map(([id, name]) => (
                  <option key={id} value={id}>{formatProjectDisplayName(id, name)}</option>
                ))}
              </Form.Select>
              {selectedProjectId && (
                <FolderBrowser
                  key={selectedProjectId}
                  rootNode={exploreData?.projects[selectedProjectId] ?? EMPTY_NODE}
                  label={formatProjectDisplayName(selectedProjectId, exploreData?.projectNames[selectedProjectId])}
                  section='projects'
                  projectId={selectedProjectId}
                  onRefresh={refreshData}
                  instance={instance}
                  account={accounts[0]}
                  uploading={uploading}
                  setUploading={setUploading}
                  setError={setError}
                />
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Col>

      <Col xl={3}>
        <RecentActivity />
      </Col>
    </Row>
  );
};

export default FileTransferPage;
